// Playbook Phase B.2 — PTP (Play The Play) timeline foundation. Pure,
// framework-free helpers for the Play document's top-level `timeline`
// structure: keyframe list management, sparse per-actor timeline state,
// continuous scrub-position resolution, and arc-length path
// interpolation. Nothing in this file knows about React, Supabase, or
// field-side transforms — it only ever reads/writes plain data and
// screen-space point arrays the caller already computed, so the same
// functions can later be reused by a read-only Athlete Study surface
// exactly as-is (see this project's established renderer/editor-
// separation precedent — FootballField.js/PlaybookMarker.js are the
// presentation-only equivalent for markers).
//
// TERMINOLOGY: the individual stops in the timeline are called
// KEYFRAMES / MARKED POINTS, never "PTPs" — PTP refers to the overall
// playback/scrubbing experience, not any one stop within it.
//
// TIMELINE LOCATION: `document.timeline`, a TOP-LEVEL sibling of
// `our`/`opponent`/`post_snap` — deliberately NOT nested inside
// post_snap, because a future Pre-Snap phase extends the same timeline
// backward across the Snap boundary.
//
// ─────────────────────────────────────────────────────────────────────
// CONTINUOUS SCRUBBER CORRECTION PASS — the scrubber's position and a
// "marked point" are now two genuinely separate concepts:
//   SCRUB POSITION — a continuous float 0.0 (Snap) .. 1.0 (End) the
//     coach can stop at ANYWHERE, never forced to snap to a keyframe.
//     This is pure UI state (see TeamPlaybookPlayEditor.js's own
//     `scrubPosition`), never persisted in the document itself.
//   MARKED POINT (keyframe) — a saved landmark AT a specific
//     `position` on that same 0..1 continuum, created intentionally via
//     "+ Mark This Point". Persistent actor authoring (explicit
//     progress/override) only ever attaches to a marked point's id,
//     never to a bare scrub position — see resolveActorAtScrub() below
//     for how a position that ISN'T a marked point still resolves
//     sensibly by interpolating between its neighboring marked points.
// ─────────────────────────────────────────────────────────────────────
//
// PHASE B.3 EXTENSION — this file's own timeline still only ever
// represents ONE phase's worth of 0..1 progress at a time; it has NO
// concept of "Pre-Snap" or "Post-Snap" built into it, deliberately (see
// §64 of the B.3 spec: "a clean composition might be playbookLifecycle.js
// USES playbookTimeline.js rather than replacing it"). What changed:
// coach-created `kind: 'keyframe'` entries now carry an optional `phase:
// 'pre_snap' | 'post_snap'` tag (see addKeyframeAtPosition() below) so
// ONE shared `timeline.keyframes` array can hold Moments from BOTH
// phases at once, each still using ITS OWN phase-local 0..1 `position`
// scale — a pre_snap position and a post_snap position are never
// directly comparable numbers. Every function in this file
// (sortedKeyframes/findKeyframeAtPosition/bracketKeyframesForPosition/
// etc.) stays deliberately PHASE-OBLIVIOUS: callers (TeamPlaybookPlayEditor.js,
// via playbookLifecycle.js's keyframesForPhase()) are responsible for
// filtering `timeline.keyframes` down to one phase's subset BEFORE
// passing it to any of these — exactly the same one-directional
// composition this file's Snap/End reserved-keyframe design already
// established. A keyframe with no `phase` field (every Moment saved
// before B.3 existed) is always interpreted as `'post_snap'` — see
// keyframePhase() in playbookLifecycle.js — so no existing saved Play's
// Moments change meaning.
//
// A coach-created `kind: 'keyframe'` entry carries an optional `cues`
// array for Moment-owned decision cues — a small history here, since
// this model has moved twice:
//   Phase B.3e introduced it (a Passing Read cue nested on the Moment
//     that "owned" it, with a fixed real-time visibility window).
//   Phase B.3f REMOVED it — browser testing at the time suggested a
//     Passing Read was fundamentally a route-attached WINDOW (a span of
//     the target's own route progress) rather than tied to any one
//     Moment's instant, so `pass_read` cues moved OUT to
//     `post_snap.passing_reads` instead.
//   Phase B.3g REINSTATES `keyframe.cues` — further tablet-first
//     browser testing showed B.3f's two-tap route-window authoring was
//     too many interactions, and that a Passing Read really is best
//     modeled as an ACTION a QB takes AT a Moment ("WHEN does the QB
//     evaluate this read?"), i.e. genuinely owned by the Moment after
//     all — while keeping B.3f's one good idea (the cue line points at
//     a route-attached landmark, not the receiver marker) via a single
//     captured `route_progress` on the cue instead of a two-point
//     window. See utils/playbook.js's own header note for the current
//     `pass_read` cue shape, and TeamPlaybookPlayEditor.js's
//     commitPassingRead()/visiblePassingReadCue for how it's authored
//     and evaluated. `post_snap.passing_reads` (the B.3f-era location)
//     is now itself legacy/unread — any such data loads harmlessly and
//     inertly, never migrated or rewritten automatically (see
//     utils/playbook.js). Deleting a keyframe (deleteKeyframe() below)
//     removes its `cues` along with it for free — a Passing Read has no
//     independent existence apart from the Moment that owns it, exactly
//     matching the "clear ownership by a Moment" requirement this model
//     exists to satisfy.
//
// SHAPE (all additive/optional — see ensureTimeline() below):
//   timeline: {
//     keyframes: [ { id, order, kind: 'snap'|'end'|'keyframe', label, position, phase?, cues? } ],
//     actor_state: {
//       "our:<markerId>": {
//         "<keyframeId>": { progress: number } | { override: { x, y } }
//       },
//       "opponent:<markerId>": { ... }
//     }
//   }
// `position` is a normalized float 0..1 — Snap is always exactly 0, End
// is always exactly 1, coach-created marked points sit strictly between.
// This is NOT real time — no seconds/timestamps anywhere. `order` is
// kept only as a denormalized, purely cosmetic mirror of the
// position-sorted array index (see withOrder() below) — `position` is
// the single source of truth for placement; the two are never allowed
// to disagree, since `order` is always freshly recomputed FROM the
// position-sorted list, never read independently of it.
//
// `actor_state` remains DELIBERATELY sparse — an actor/keyframe
// combination with nothing explicit simply isn't a key here at all.
// Derived default progress at a marked point equals that point's own
// `position` (see resolveActorAtScrub() below) and is NEVER written
// into this structure. `override` coordinates are always in the actor's
// OWN native per-side field convention (never screen pixels, never
// pre-mirrored for `opponent`) — identical convention to every other
// coordinate in the Play document.
//
// SNAP + END: every timeline created by ensureTimeline() gets exactly
// one reserved, non-deletable, non-renameable `kind: 'snap'` keyframe at
// `position: 0`, and one reserved, non-deletable, non-renameable
// `kind: 'end'` keyframe at `position: 1` — see this file's own note
// above addKeyframeAtPosition() for why Snap+End (rather than Snap
// alone) was chosen. Coach-created marked points are `kind: 'keyframe'`
// and always sit strictly between 0 and 1.

import { newMarkerId } from './playbookMarkers';

export const newKeyframeId = newMarkerId;

/** The full set of assignment types (see TeamPlaybookPlayEditor.js's own
 * ASSIGNMENT_TYPE_META) that carry a `path` a timeline scrub can walk
 * along. Kept as an independent constant here — rather than imported
 * from the page — since utils must never import back from a page module
 * (the same one-directional-import discipline already established
 * between fieldCoordinates.js and PlaybookMarker.js). Whoever adds a new
 * path-bearing assignment type in the page must also add it here. */
export const PATH_BASED_TIMELINE_TYPES = new Set([
  'route', 'carry', 'movement', 'rush', 'blitz', 'run_fit',
  'block', 'run_block', 'pass_block', 'handoff', 'pass',
  // Phase B.3 — Pre-Snap Motion/Shift finally has a real authoring UI
  // (TeamPlaybookPlayEditor.js's own `pre_snap.assignments`), so its
  // long-reserved 'motion' type (see ASSIGNMENT_TYPE_META's own note in
  // that file) is now genuinely path-based/timeline-walkable, exactly
  // like every Post-Snap path type above.
  'motion',
]);

export function isPathBasedTimelineType(type) {
  return PATH_BASED_TIMELINE_TYPES.has(type);
}

// The scrubber's slider step, and how close a scrub position has to be
// to a marked point's own `position` to count as "on" it (half a step —
// see findKeyframeAtPosition()/§14's "within slider tolerance"). Shared
// here so the page's slider, nudge buttons, and "already a point here?"
// duplicate check can never drift out of sync with each other.
export const SCRUB_STEP = 0.01;
export const POSITION_MATCH_TOLERANCE = SCRUB_STEP / 2;

function withOrder(list) {
  return list.map((k, i) => ({ ...k, order: i }));
}

/** Always-sorted read of a timeline's keyframes, by `position` — the
 * single source of truth for placement (see this file's own header
 * note on why `order` is never read independently). */
export function sortedKeyframes(timeline) {
  return [...(timeline?.keyframes || [])].sort((a, b) => a.position - b.position);
}

export function isProtectedKeyframe(keyframe) {
  return keyframe?.kind === 'snap' || keyframe?.kind === 'end';
}

/** The nearest keyframe to `position` within `tolerance` (default
 * POSITION_MATCH_TOLERANCE), or null if the scrub position doesn't
 * currently sit "on" any saved marked point. `keyframes` must already
 * be position-sorted (sortedKeyframes()). This is what determines
 * whether the coach is currently "at" Snap/End/a marked point (and
 * therefore whether per-actor authoring controls are available) versus
 * freely previewing an unmarked moment in the play. */
export function findKeyframeAtPosition(keyframes, position, tolerance = POSITION_MATCH_TOLERANCE) {
  let best = null;
  let bestDist = Infinity;
  for (const k of keyframes) {
    const dist = Math.abs(k.position - position);
    if (dist <= tolerance && dist < bestDist) {
      best = k;
      bestDist = dist;
    }
  }
  return best;
}

/** Backfills a well-formed, position-normalized `timeline` on a Play
 * document — never mutates the input, never requires a migration, and
 * is intentionally NOT called from emptyPlayDocument()/persisted
 * anywhere automatically (see ensurePostSnap()'s own established
 * pattern) — this only runs at document-LOAD time in PlayEditorView's
 * own state initializer, so an unopened Play's stored document is never
 * silently rewritten; normalization only persists on the coach's own
 * next Save. Two cases:
 *   1. No timeline at all (every Play saved before B.2 existed) —
 *      create a fresh Snap(0)/End(1) timeline.
 *   2. A timeline already exists but predates the continuous-scrubber
 *      correction, so its keyframes have no `position` field yet — back-
 *      fill Snap->0, End->1, and every existing coach-created keyframe
 *      using the OLD evenly-spaced index/(count-1) formula, so a
 *      previously-saved Snap/Step1/Step2/End normalizes to
 *      0 / 0.333.. / 0.667.. / 1, preserving exactly where those points
 *      already were before this pass existed. */
export function ensureTimeline(doc) {
  const existing = doc.timeline;
  if (!existing || !Array.isArray(existing.keyframes) || !existing.keyframes.some(k => k.kind === 'snap')) {
    return {
      ...doc,
      timeline: {
        keyframes: withOrder([
          { id: newKeyframeId(), kind: 'snap', label: 'Snap', position: 0 },
          { id: newKeyframeId(), kind: 'end', label: 'End', position: 1 },
        ]),
        actor_state: {},
      },
    };
  }
  if (existing.keyframes.every(k => typeof k.position === 'number')) {
    return doc; // already normalized — no-op, matches doc identity for cheap re-renders
  }
  const legacySorted = [...existing.keyframes].sort((a, b) => a.order - b.order);
  const n = legacySorted.length;
  const normalized = legacySorted.map((k, i) => {
    if (typeof k.position === 'number') return k;
    if (k.kind === 'snap') return { ...k, position: 0 };
    if (k.kind === 'end') return { ...k, position: 1 };
    return { ...k, position: n > 1 ? i / (n - 1) : 0 };
  });
  return { ...doc, timeline: { ...existing, keyframes: withOrder(normalized) } };
}

// WHY SNAP + END BY DEFAULT (rather than Snap alone): a reserved End
// keyframe fixed at position 1 gives every path-based actor a
// guaranteed progress=1.0 landing point without any special-casing.
// Snap alone would have left "how does an actor ever reach 1.0" an
// implicit convention the coach would have to solve themselves. End is
// protected the same way Snap is (non-deletable, non-renameable) so
// this guarantee can never be broken by ordinary keyframe editing.

/** "+ Mark Moment" (§13 of the B.2.1 UX pass) — inserts a new coach-
 * created `kind: 'keyframe'` at the EXACT given `position`, without
 * moving or redistributing any existing keyframe. Label defaults to
 * "Moment N" (N = 1-based count of existing coach-created keyframes +
 * 1) — this is a coach-facing TERMINOLOGY change only (§3): a Play
 * saved before this pass with an older "Step N" label keeps that label
 * verbatim forever (nothing rewrites existing labels; only the DEFAULT
 * for newly-created ones changed) — `kind: 'keyframe'` itself is
 * unchanged internal naming, deliberately kept per the spec's own "keep
 * internal technical naming where useful" instruction. Callers are
 * expected to have already checked findKeyframeAtPosition() for a
 * near-duplicate (see the page's own markThisPoint()) — this function
 * itself does not de-duplicate, so it stays a simple, predictable
 * primitive other callers can also use safely. */
/** Phase B.3 (§40/§41) — `phase` tags the new keyframe as belonging to
 * the Pre-Snap or Post-Snap portion of the FULL lifecycle. Defaults to
 * `'post_snap'` so every pre-B.3 call site (and every Post-Snap Moment
 * this file already created before Pre-Snap existed) keeps behaving
 * identically without passing a 4th argument. `position` remains
 * PHASE-LOCAL (0..1 within whichever phase it belongs to) — a Pre-Snap
 * Moment's position and a Post-Snap Moment's position are NOT on the
 * same numeric scale (see playbookLifecycle.js's own header note), so
 * callers must always pre-filter `timeline.keyframes` by phase before
 * doing anything position-based with them (sortedKeyframes/
 * findKeyframeAtPosition/bracketKeyframesForPosition all remain
 * deliberately phase-OBLIVIOUS pure array ops — the caller filters).
 * "Moment N" numbering stays a single GLOBAL counter across both phases
 * (matches the product's own "one continuous lifecycle timeline"
 * framing — a coach thinks of Moments as one ordered set of bookmarks,
 * not two separate per-phase counters). */
export function addKeyframeAtPosition(timeline, position, label, phase = 'post_snap') {
  const kfs = sortedKeyframes(timeline);
  const momentCount = kfs.filter(k => k.kind === 'keyframe').length;
  const phaseRank = k => (k.phase === 'pre_snap' ? 0 : 1);
  const next = withOrder(
    [...kfs, { id: newKeyframeId(), kind: 'keyframe', position, label: label || `Moment ${momentCount + 1}`, phase }]
      // Sorted PRE-SNAP-then-POST-SNAP first (chronological lifecycle
      // order), then by each keyframe's own phase-local position — never
      // compares a pre_snap position directly against a post_snap one.
      .sort((a, b) => phaseRank(a) - phaseRank(b) || a.position - b.position),
  );
  return { ...timeline, keyframes: next };
}

/** Snap/End are never renameable — this is a silent no-op for them
 * rather than an error, so a careless call site can't crash the editor
 * over a keyframe id that happens to be protected. */
export function renameKeyframe(timeline, keyframeId, label) {
  return {
    ...timeline,
    keyframes: (timeline.keyframes || []).map(k => (
      k.id === keyframeId && k.kind === 'keyframe' ? { ...k, label } : k
    )),
  };
}

/** Deletes a coach-created marked point (silent no-op for Snap/End) and
 * prunes every sparse actor_state entry tied to it — no dangling state
 * is ever left behind for a keyframe id that no longer exists. */
export function deleteKeyframe(timeline, keyframeId) {
  const kf = (timeline.keyframes || []).find(k => k.id === keyframeId);
  if (!kf || isProtectedKeyframe(kf)) return timeline;
  const nextKeyframes = withOrder(sortedKeyframes(timeline).filter(k => k.id !== keyframeId));
  const actor_state = {};
  for (const [actorKey, perKeyframe] of Object.entries(timeline.actor_state || {})) {
    const rest = { ...perKeyframe };
    delete rest[keyframeId];
    if (Object.keys(rest).length > 0) actor_state[actorKey] = rest;
  }
  return { ...timeline, keyframes: nextKeyframes, actor_state };
}

/** Every cue attached to one keyframe/Moment — sparse (most keyframes
 * have none). Never throws on a keyframe with no `cues` field at all
 * (every keyframe saved before B.3e/B.3g existed, or Snap/End, which
 * never carry cues). Phase B.3g. */
export function getMomentCues(keyframe) {
  return keyframe?.cues || [];
}

/** Replaces the FULL `cues` array on one keyframe (immutable, silent
 * no-op if the keyframe id doesn't exist). Deliberately generic over
 * cue shape/type — this file only ever relocates an array onto the
 * right keyframe object; it has no opinion on what a "cue" contains or
 * means (`type: 'pass_read'` today, a broader Decision Cue/Path Action
 * union potentially later — see TeamPlaybookPlayEditor.js's own
 * commitPassingRead()/removeMomentPassingRead() for the one caller that
 * currently exists and owns the pass_read-specific replace-by-
 * progression identity rule). Phase B.3g. */
export function replaceKeyframeCues(timeline, keyframeId, cues) {
  return {
    ...timeline,
    keyframes: (timeline.keyframes || []).map(k => (k.id === keyframeId ? { ...k, cues } : k)),
  };
}

/** The two ADJACENT keyframes (by position) bracketing a scrub
 * `position`, plus how far between them it sits (0..1). `keyframes`
 * must already be position-sorted. Since Snap(0)/End(1) always exist
 * and bound the whole range, this always returns a valid bracket. When
 * `position` lands exactly on (or within POSITION_MATCH_TOLERANCE of) a
 * keyframe, `a` and `b` are the SAME keyframe and `frac` is 0 — callers
 * should treat that as "resolve this one keyframe directly," not as an
 * interpolation (see resolveActorAtScrub() in the page for exactly this
 * check). */
export function bracketKeyframesForPosition(keyframes, position) {
  if (!keyframes || keyframes.length === 0) return null;
  const exact = findKeyframeAtPosition(keyframes, position);
  if (exact) return { a: exact, b: exact, frac: 0 };
  if (keyframes.length === 1) return { a: keyframes[0], b: keyframes[0], frac: 0 };
  let a = keyframes[0];
  let b = keyframes[keyframes.length - 1];
  for (let i = 0; i < keyframes.length - 1; i++) {
    if (keyframes[i].position <= position && keyframes[i + 1].position >= position) {
      a = keyframes[i];
      b = keyframes[i + 1];
      break;
    }
  }
  const span = b.position - a.position;
  const frac = span > 0 ? Math.max(0, Math.min(1, (position - a.position) / span)) : 0;
  return { a, b, frac };
}

/** "our:<id>" / "opponent:<id>" — mirrors this project's established
 * actor_side+actor_id pairing (see TeamPlaybookPlayEditor.js's own
 * header note on why a bare marker id is never safe alone: the two
 * sides' marker arrays are independent and never guaranteed unique
 * against each other). Never role_label — role_label is free-text and
 * renamable, never identity. */
export function actorStateKey(side, id) {
  return `${side}:${id}`;
}

export function getActorKeyframeState(timeline, side, id, keyframeId) {
  return timeline?.actor_state?.[actorStateKey(side, id)]?.[keyframeId] || null;
}

export function setActorKeyframeProgress(timeline, side, id, keyframeId, progress) {
  const key = actorStateKey(side, id);
  const perActor = { ...(timeline.actor_state?.[key] || {}), [keyframeId]: { progress } };
  return { ...timeline, actor_state: { ...(timeline.actor_state || {}), [key]: perActor } };
}

export function setActorKeyframeOverride(timeline, side, id, keyframeId, xy) {
  const key = actorStateKey(side, id);
  const perActor = { ...(timeline.actor_state?.[key] || {}), [keyframeId]: { override: { x: xy.x, y: xy.y } } };
  return { ...timeline, actor_state: { ...(timeline.actor_state || {}), [key]: perActor } };
}

/** "Use Default" — removes ONE actor's explicit state at ONE keyframe,
 * returning them to derived progress (or static, if they have no
 * path-based assignment). Cleans up the now-empty per-actor bucket too,
 * so a fully-defaulted actor leaves zero trace in actor_state rather
 * than an empty `{}` hanging around forever. */
export function clearActorKeyframeState(timeline, side, id, keyframeId) {
  const key = actorStateKey(side, id);
  const perActor = { ...(timeline.actor_state?.[key] || {}) };
  delete perActor[keyframeId];
  const actor_state = { ...(timeline.actor_state || {}) };
  if (Object.keys(perActor).length > 0) actor_state[key] = perActor;
  else delete actor_state[key];
  return { ...timeline, actor_state };
}

/** Actor-deletion cleanup — drops every actor_state bucket whose
 * side+id no longer exists, per `existsFn(side, id) -> boolean`. No
 * current Play-editing UI removes a starting marker yet (the same is
 * true of pruneAssignments() in TeamPlaybookPlayEditor.js, which this
 * mirrors), so this is a reusable helper ready to be wired in the
 * moment that becomes possible — not a speculative subsystem. */
export function pruneTimelineActors(timeline, existsFn) {
  const actor_state = {};
  for (const [actorKey, perKeyframe] of Object.entries(timeline.actor_state || {})) {
    const sep = actorKey.indexOf(':');
    const side = actorKey.slice(0, sep);
    const id = actorKey.slice(sep + 1);
    if (existsFn(side, id)) actor_state[actorKey] = perKeyframe;
  }
  return { ...timeline, actor_state };
}

/** Actor-deletion cleanup for Moment-owned cues (Phase B.3g) — mirrors
 * pruneTimelineActors() immediately above, which only prunes
 * `actor_state`; a keyframe's `cues` array is a separate structure that
 * needs the same treatment. Drops any cue whose owning actor OR target
 * no longer exists, per `existsFn(side, id) -> boolean`. Same "ready the
 * moment marker deletion becomes possible" reasoning as
 * pruneTimelineActors() itself — no current Play-editing UI removes a
 * starting marker yet. Generic over cue shape beyond `actor_side`/
 * `actor_id`/`target_side`/`target_actor_id` (a target is optional —
 * not every future cue type will have one). */
export function pruneMomentCues(timeline, existsFn) {
  const keyframes = (timeline.keyframes || []).map(k => {
    if (!k.cues || k.cues.length === 0) return k;
    const cues = k.cues.filter(c => {
      const actorOk = existsFn(c.actor_side, c.actor_id);
      const targetOk = !c.target_actor_id || existsFn(c.target_side, c.target_actor_id);
      return actorOk && targetOk;
    });
    return cues.length === k.cues.length ? k : { ...k, cues };
  });
  return { ...timeline, keyframes };
}

/** Arc-length-weighted walk along an ALREADY SCREEN-TRANSFORMED polyline
 * (points: [{px,py}, ...], length >= 1). Pure geometry — no coordinate-
 * convention knowledge at all, deliberately: field-space x (normalized
 * -1..1) and y (yards) are NOT the same unit scale, so weighting an
 * interpolation by raw field-coordinate deltas would misjudge distance
 * (a corner route's short lateral step and long vertical stem would be
 * compared on incompatible scales). Screen/viewBox space is isotropic
 * (both axes already scaled into the same pixel units by
 * fieldToScreen()/opponentFieldToScreen()), so THAT'S where arc-length
 * math is valid — this is exactly why blockContactPoint() in
 * TeamPlaybookPlayEditor.js already does its own distance math in
 * screen space today. Callers convert the result back to field
 * coordinates via the matching inverse transform; this function itself
 * never needs to know which side or which transform was used. */
export function pointAlongPolyline(points, t) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) return points[0];
  const clamped = Math.max(0, Math.min(1, t));
  const segLens = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const len = Math.hypot(points[i].px - points[i - 1].px, points[i].py - points[i - 1].py);
    segLens.push(len);
    total += len;
  }
  if (total === 0) return points[0];
  let remaining = clamped * total;
  for (let i = 0; i < segLens.length; i++) {
    const segLen = segLens[i];
    if (remaining <= segLen || i === segLens.length - 1) {
      const segT = segLen === 0 ? 0 : Math.min(1, remaining / segLen);
      const a = points[i];
      const b = points[i + 1];
      return { px: a.px + (b.px - a.px) * segT, py: a.py + (b.py - a.py) * segT };
    }
    remaining -= segLen;
  }
  return points[points.length - 1];
}

/** Phase B.3f — the inverse operation of pointAlongPolyline() above:
 * given an arbitrary SCREEN-SPACE `point` (e.g. a coach's field tap),
 * finds the NEAREST point on a screen-space polyline plus that point's
 * own normalized arc-length progress (0..1) along it. Deliberately
 * shares pointAlongPolyline()'s exact distance semantics (§46 of the
 * B.3f spec: "must be based on distance along the polyline, not
 * waypoint index") — both walk the SAME per-segment arc lengths, so a
 * `t` returned here and fed back into pointAlongPolyline() lands
 * (modulo the projection's own perpendicular snap) on the same point.
 * Pure geometry, no coordinate-convention or football knowledge, same
 * isotropic-screen-space reasoning as pointAlongPolyline()'s own header
 * note. Originally added for B.3f's two-tap route-window authoring UI,
 * which B.3g removed in favor of Moment-based Passing Read timing (see
 * this file's own header note on that history) — kept here regardless
 * as a genuinely general-purpose polyline utility with no current
 * caller, rather than deleted, since "nearest point + progress along a
 * screen-space polyline" is exactly the kind of primitive a future
 * route-tap feature (or Path Action authoring) would reach for again.
 *
 * Returns `{ px, py, t, distance, segmentIndex }` — `distance` is the
 * raw screen-space (viewBox-unit) gap between `point` and the nearest
 * point ON the polyline. Deliberately does NOT apply any "close enough"
 * tolerance itself — that would be an authoring-UX judgment call for
 * whichever caller eventually needs one, not a property of the
 * geometry. Returns null for an empty polyline. */
export function projectPointOntoPolyline(points, point) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) {
    return {
      px: points[0].px, py: points[0].py, t: 0,
      distance: Math.hypot(point.px - points[0].px, point.py - points[0].py),
      segmentIndex: 0,
    };
  }
  const segLens = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    segLens.push(Math.hypot(points[i].px - points[i - 1].px, points[i].py - points[i - 1].py));
    total += segLens[i - 1];
  }
  let best = null;
  let bestDist = Infinity;
  let traveled = 0;
  for (let i = 0; i < segLens.length; i++) {
    const a = points[i];
    const b = points[i + 1];
    const segLen = segLens[i];
    let segT = 0;
    if (segLen > 0) {
      const abx = b.px - a.px;
      const aby = b.py - a.py;
      const apx = point.px - a.px;
      const apy = point.py - a.py;
      segT = Math.max(0, Math.min(1, (apx * abx + apy * aby) / (segLen * segLen)));
    }
    const projX = a.px + (b.px - a.px) * segT;
    const projY = a.py + (b.py - a.py) * segT;
    const dist = Math.hypot(point.px - projX, point.py - projY);
    if (dist < bestDist) {
      bestDist = dist;
      const arc = traveled + segLen * segT;
      best = { px: projX, py: projY, t: total > 0 ? arc / total : 0, distance: dist, segmentIndex: i };
    }
    traveled += segLen;
  }
  return best;
}
